// Shared scaffold for the Speakers / Presidents Explore-stats pages
// (and any future "office holder tenure" page). They share the same
// backend payload shape (`_leader_stats`-style charts + table_rows +
// _links) so all three become near-identical thin wrappers.

import React, { useState, useEffect } from 'react';
import { Paper, Typography, Grid } from '@mui/material';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatBar } from './charts';

/**
 * @param slug       Backend endpoint slug (e.g. "speakers").
 * @param title      Page title (e.g. "Speakers of the House of Representatives").
 * @param csvName    CSV download stem (optional, defaults to slug).
 * @param description Optional descriptive blurb above the charts; rendered
 *                   under the title with a "(N total)" suffix when known.
 */
export default function OfficeHolderTenure({ slug, title, csvName, description }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat(slug);
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [slug]);

  const c = data?.charts || {};
  const totalSuffix = c.total ? ` (${c.total} total)` : '';

  return (
    <ExploreStatsPage
      title={title}
      description={`${description || ''}${totalSuffix}`}
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName={csvName || slug}
    >
      {data && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {title} by party and cumulative length of service (years)
              </Typography>
              <StatBar data={c.by_party_service || []} xKey="party"
                       yKey="years" layout="vertical" height={340} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Number of office holders by party
              </Typography>
              <StatBar data={c.count_by_party || []} xKey="party"
                       yKey="count" layout="vertical" height={340} />
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Longest tenures (years)
              </Typography>
              <StatBar data={c.longest_tenure || []} xKey="name"
                       yKey="years" layout="horizontal" height={320} />
            </Paper>
          </Grid>
        </Grid>
      )}
    </ExploreStatsPage>
  );
}
